import { ActivityLog, type Contract, Event, Membership } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  toast,
  useConfirm,
} from '@kalos/ui';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { FieldFullScreenDialog } from '../../../../components/FieldFullScreenDialog';
import { useAuth } from '../../../../context/AuthContext';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { useContractsBatchGetQuery } from '../../../../hooks/react-query/useContractsQuery';
import { useFieldLayout } from '../../../../hooks/useMedia';
import {
  CONTRACT_BADGE_CLASS,
  type ContractBadgeStatus,
  contractBadgeStatus,
} from '../../../../tools/contractStatus';
import {
  ActivityLogClientService,
  EventClientService,
  MembershipClientService,
  timestamp,
} from '../../../../tools/helpers';
import { StatusBadge } from '../../../Memberships/components/StatusBadge';
import { useAssignSoldOnJob } from '../../../Memberships/hooks/useAssignSoldOnJob';
import { useMembershipStatuses } from '../../../Memberships/hooks/useMembershipStatuses';
import { mapMembershipStatus } from '../../../Memberships/utils/mapContractStatus';
import { membershipPlanLabel } from '../../../Memberships/utils/membershipPlanLabel';

interface Props {
  open: boolean;
  onClose: () => void;
  event: Event;
  customerId: number;
  onSuccess: () => void;
  /**
   * Opt-in: show a contract's memberships so they can be recorded as sold on this job. Off for the
   * legacy ServiceCall page, which mounts this same dialog.
   */
  allowMembershipMatch?: boolean;
}

/**
 * Only a live contract's memberships can be matched. Contract carries no archive flag, and
 * cancelled is a subset of inactive (`!isActive && reviewStatus !== 'pending'`), so `isActive` is
 * the whole rule.
 */
const canMatchMemberships = (contract: Contract) => contract.isActive === 1;

/** Select's warning for each Status badge other than Active. */
const SELECT_WARNING: Record<Exclude<ContractBadgeStatus, 'Active'>, string> = {
  Inactive:
    'This contract is marked as inactive. Are you sure you want to assign a job to an inactive contract?',
  'Not active yet':
    'This contract has not started yet. Are you sure you want to assign a job to a future contract?',
  Expired:
    'This contract has expired. Are you sure you want to assign a job to an expired contract?',
};

/** Memberships on one contract, tickable so a tech can record which of them this job sold. */
const MembershipMatchSection: React.FC<{
  contractId: number;
  eventId: number;
}> = ({ contractId, eventId }) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const assignSoldOnJob = useAssignSoldOnJob();
  const statusesQuery = useMembershipStatuses({ enabled: contractId > 0 });

  const membershipsQuery = useQuery({
    queryKey: [queryKeys.memberships.root, 'contractMemberships', contractId],
    queryFn: async () => {
      const res = await MembershipClientService.BatchGet(
        Membership.create({
          contractId,
          withType: true,
          withContract: true,
          withProperty: true,
          // Commercial plans carry their name on the instance lines, not the catalog type.
          withLines: true,
          withoutLimit: true,
        }),
      );
      return res?.results ?? [];
    },
    enabled: contractId > 0,
  });

  const rows = membershipsQuery.data ?? [];
  // One already pointing here needs no write, so it is never selectable.
  const matchable = rows.filter((m) => m.soldOnEventId !== eventId);
  const allSelected = matchable.length > 0 && selectedIds.length === matchable.length;

  const toggle = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = () =>
    assignSoldOnJob.mutate(
      { membershipIds: selectedIds, eventId },
      {
        // Leave the ones that failed ticked so a retry doesn't have to re-pick them.
        onSuccess: ({ recorded, failed }) => {
          setSelectedIds(failed);
          if (failed.length > 0) return;
          toast({
            title:
              recorded.length === 1
                ? 'Membership matched to this job'
                : `${recorded.length} memberships matched to this job`,
            variant: 'success',
          });
        },
      },
    );

  return (
    <div className="min-w-0 rounded-md border bg-gray-50 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">Tick the memberships that were sold on this job.</p>
        {matchable.length > 0 && (
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={selectedIds.length === 0 || assignSoldOnJob.isPending}
          >
            {assignSoldOnJob.isPending ? 'Recording…' : 'Match selected to this job'}
          </Button>
        )}
      </div>

      {membershipsQuery.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      ) : membershipsQuery.isError ? (
        <p className="text-sm text-red-600">Couldn&apos;t load this contract&apos;s memberships.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-600">This contract has no memberships.</p>
      ) : (
        <div className="space-y-1">
          {matchable.length > 1 && (
            <label className="flex items-center gap-2 px-1 text-xs font-medium">
              <Checkbox
                checked={allSelected}
                disabled={assignSoldOnJob.isPending}
                onCheckedChange={() =>
                  setSelectedIds(allSelected ? [] : matchable.map((m) => m.id))
                }
              />
              Select all
            </label>
          )}
          {rows.map((m) => {
            const isHere = m.soldOnEventId === eventId;
            const { status } = mapMembershipStatus(m, m.contract, statusesQuery.data);
            const propertyLabel =
              [m.property?.address, m.property?.city].filter(Boolean).join(', ') ||
              `Property #${m.propertyId}`;
            return (
              <div
                key={m.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm"
              >
                <Checkbox
                  checked={selectedIds.includes(m.id)}
                  disabled={isHere || assignSoldOnJob.isPending}
                  onCheckedChange={() => toggle(m.id)}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">
                    {membershipPlanLabel(m, m.membershipType?.name)}
                  </div>
                  <div className="truncate text-xs text-gray-500">{propertyLabel}</div>
                </div>
                <StatusBadge status={status} />
                <span className="shrink-0 text-xs text-gray-500">
                  {isHere
                    ? 'Sold on this job'
                    : m.soldOnEventId > 0
                      ? `Job #${m.soldOnEventId}`
                      : 'Not recorded'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ContractNumberDialog: React.FC<Props> = ({
  open,
  onClose,
  event,
  customerId,
  onSuccess,
  allowMembershipMatch = false,
}) => {
  const auth = useAuth();
  // Which contract's memberships are on screen — deliberately not the same thing as assigning the
  // contract to the job, which is what the Select button below still does.
  const [viewingContractId, setViewingContractId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isFieldLayout = useFieldLayout();
  const confirm = useConfirm();

  // Fetch contracts for the customer, ordered by most recent
  const contractsQuery = useContractsBatchGetQuery({
    filter: {
      userId: customerId,
      orderBy: 'contract_date_created',
      orderDir: 'desc',
    },
    enabled: open && !!customerId && customerId > 0,
  });

  // Start on the contract the job already carries; otherwise nothing is shown until one is picked.
  useEffect(() => {
    if (open) setViewingContractId(event.contractId || 0);
  }, [open, event.contractId]);

  const handleSelectContract = useCallback(
    async (contract: Contract) => {
      if (!contract.id) {
        toast({
          title: 'Error',
          description: 'Invalid contract selected. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      const status = contractBadgeStatus(contract);
      if (status !== 'Active') {
        toast({ title: 'Warning', description: SELECT_WARNING[status], variant: 'destructive' });
      }

      const label = contract.number || `C${contract.id}`;
      const confirmed = await confirm({
        title: 'Confirm Contract Assignment',
        body: (
          <div className="space-y-2 text-sm">
            <p>Add this job to contract #{label}?</p>
            <p className="text-gray-600">
              This updates the job&apos;s contract ID and number, and creates an activity log entry.
            </p>
          </div>
        ),
        actionButton: 'Confirm',
        cancelButton: 'Cancel',
      });
      if (!confirmed || !event.id) return;

      setIsLoading(true);
      try {
        // Update the event with the contract information
        const updatedEvent = Event.clone(event);
        updatedEvent.contractId = contract.id;
        updatedEvent.contractNumber = label;
        updatedEvent.fieldMask = ['ContractId', 'ContractNumber'];

        await EventClientService.Update(updatedEvent);

        // Create activity log
        const activity = ActivityLog.create();
        activity.eventId = event.id;
        const userName =
          auth.user.firstname && auth.user.lastname
            ? `${auth.user.firstname} ${auth.user.lastname}`
            : `User ID ${auth.user.id}`;
        activity.activityName = `Job added to contract #${label} by ${userName}`;
        activity.activityDate = timestamp();
        activity.userId = auth.user.id;

        await ActivityLogClientService.Create(activity);

        toast({
          title: 'Success',
          description: `Job has been added to contract #${label}`,
          variant: 'success',
        });

        onSuccess();
        // Stay open: assigning the contract is what reveals its memberships.
        setViewingContractId(contract.id);
      } catch (error) {
        console.error('Failed to update contract number:', error);
        toast({
          title: 'Error',
          description: 'Failed to update contract number. Please try again.',
          variant: 'destructive',
        });
        // Don't close the dialog on error, let user try again
      } finally {
        setIsLoading(false);
      }
    },
    [confirm, event, auth.user.firstname, auth.user.lastname, auth.user.id, onSuccess],
  );

  const handleClearContract = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Clear Contract Number',
      body: (
        <div className="space-y-2 text-sm">
          <p>Clear the contract number from this job?</p>
          <p className="text-gray-600">
            This removes the contract ID and number from the job, and creates an activity log entry.
          </p>
        </div>
      ),
      actionButton: 'Clear Contract',
      cancelButton: 'Cancel',
    });
    if (!confirmed || !event.id) return;

    setIsLoading(true);
    try {
      // Update the event to clear the contract information
      const updatedEvent = Event.clone(event);
      updatedEvent.contractId = 0;
      updatedEvent.contractNumber = '';
      updatedEvent.fieldMask = ['ContractId', 'ContractNumber'];

      await EventClientService.Update(updatedEvent);

      // Create activity log
      const activity = ActivityLog.create();
      activity.eventId = event.id;
      const userName =
        auth.user.firstname && auth.user.lastname
          ? `${auth.user.firstname} ${auth.user.lastname}`
          : `User ID ${auth.user.id}`;
      activity.activityName = `Contract number cleared from job by ${userName}`;
      activity.activityDate = timestamp();
      activity.userId = auth.user.id;

      await ActivityLogClientService.Create(activity);

      toast({
        title: 'Success',
        description: 'Contract number has been cleared from this job',
        variant: 'success',
      });

      // Nothing is assigned any more, so no contract's memberships are in scope.
      setViewingContractId(0);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to clear contract number:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear contract number. Please try again.',
        variant: 'destructive',
      });
      // Don't close the dialog on error, let user try again
    } finally {
      setIsLoading(false);
    }
  }, [confirm, event, auth.user.firstname, auth.user.lastname, auth.user.id, onSuccess, onClose]);

  const contractTableColumns: ColumnDef<Contract>[] = useMemo(
    () => [
      {
        accessorKey: 'number',
        header: ({ column }) => <DataTableColumnHeader title="Contract Number" column={column} />,
        cell: ({ row }) => {
          const isInactive = row.original.isActive === 0;
          return (
            <div className={`font-medium ${isInactive ? 'text-gray-400' : ''}`}>
              {row.original.number || `C${row.original.id}`}
            </div>
          );
        },
      },
      {
        accessorKey: 'isActive',
        header: ({ column }) => <DataTableColumnHeader title="Status" column={column} />,
        cell: ({ row }) => {
          const status = contractBadgeStatus(row.original);
          return (
            <div>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CONTRACT_BADGE_CLASS[status]}`}
              >
                {status}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'dateCreated',
        header: ({ column }) => <DataTableColumnHeader title="Date Created" column={column} />,
        cell: ({ row }) => {
          const isInactive = row.original.isActive === 0;
          try {
            if (row.original.dateCreated && row.original.dateCreated !== '0000-00-00 00:00:00') {
              const date = new Date(row.original.dateCreated);
              if (!isNaN(date.getTime())) {
                return (
                  <div className={isInactive ? 'text-gray-400' : ''}>
                    {format(date, 'MMM d, yyyy')}
                  </div>
                );
              }
            }
          } catch (error) {
            console.warn('Could not parse contract created date:', row.original.dateCreated, error);
          }
          return <div className={isInactive ? 'text-gray-400' : ''}>N/A</div>;
        },
      },
      {
        accessorKey: 'dateStarted',
        header: ({ column }) => <DataTableColumnHeader title="Start Date" column={column} />,
        cell: ({ row }) => {
          const isInactive = row.original.isActive === 0;
          try {
            if (row.original.dateStarted && row.original.dateStarted !== '0000-00-00 00:00:00') {
              const date = new Date(row.original.dateStarted);
              if (!isNaN(date.getTime())) {
                return (
                  <div className={isInactive ? 'text-gray-400' : ''}>
                    {format(date, 'MMM d, yyyy')}
                  </div>
                );
              }
            }
          } catch (error) {
            console.warn('Could not parse contract start date:', row.original.dateStarted, error);
          }
          return <div className={isInactive ? 'text-gray-400' : ''}>N/A</div>;
        },
      },
      {
        accessorKey: 'dateEnded',
        header: ({ column }) => <DataTableColumnHeader title="End Date" column={column} />,
        cell: ({ row }) => {
          const isInactive = row.original.isActive === 0;
          try {
            if (row.original.dateEnded && row.original.dateEnded !== '0000-00-00 00:00:00') {
              const date = new Date(row.original.dateEnded);
              if (!isNaN(date.getTime())) {
                return (
                  <div className={isInactive ? 'text-gray-400' : ''}>
                    {format(date, 'MMM d, yyyy')}
                  </div>
                );
              }
            }
          } catch (error) {
            console.warn('Could not parse contract end date:', row.original.dateEnded, error);
          }
          return <div className={isInactive ? 'text-gray-400' : ''}>N/A</div>;
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => {
          const contract = row.original;
          const isAlreadySelected = event.contractId === contract.id;
          const isInactive = contract.isActive === 0;

          return (
            // Fixed widths, so the pair lines up down the column instead of shifting per label.
            <div className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:justify-end">
              <Button
                size="sm"
                onClick={() => void handleSelectContract(contract)}
                disabled={isLoading || isAlreadySelected}
                variant={isAlreadySelected ? 'secondary' : isInactive ? 'outline' : 'default'}
                className={`sm:w-24 ${isInactive ? 'text-gray-600' : ''}`}
              >
                {isAlreadySelected ? 'Selected' : 'Select'}
              </Button>
              {allowMembershipMatch && (
                // Opens the memberships without touching the job's contract — the two writes stay
                // independent. Green reads as "membership" across the job page.
                <Button
                  size="sm"
                  variant={
                    !canMatchMemberships(contract)
                      ? 'outline'
                      : viewingContractId === contract.id
                        ? 'secondary'
                        : 'success'
                  }
                  onClick={() =>
                    setViewingContractId((prev) => (prev === contract.id ? 0 : contract.id))
                  }
                  disabled={!canMatchMemberships(contract)}
                  className="sm:w-32"
                >
                  {viewingContractId === contract.id ? 'Hide' : 'Memberships'}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [handleSelectContract, isLoading, event.contractId, allowMembershipMatch, viewingContractId],
  );

  const table = useReactTable({
    data: contractsQuery.data?.results || [],
    columns: contractTableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const body = (
    <div className="mt-4 min-w-0">
      {event.contractId &&
      event.contractId !== 0 &&
      event.contractNumber &&
      event.contractNumber.trim() !== '' ? (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Currently assigned to:</span> Contract #
            {event.contractNumber}
          </p>
        </div>
      ) : null}

      {contractsQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      ) : contractsQuery.error ? (
        <div className="py-8 text-center">
          <p className="text-red-600">Failed to load contracts. Please try again.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => contractsQuery.refetch()}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      ) : contractsQuery.data?.results.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-gray-600">No contracts found for this customer.</p>
        </div>
      ) : (
        // min-w-0 is load-bearing: without it the table's min-content width widens the
        // dialog's grid track and pushes every sibling past the panel padding.
        <div className="min-w-0 overflow-x-auto">
          <DataTable
            table={table}
            enableMobileLayout
            // Returning null skips the row entirely, so only the open contract mounts a
            // section — and only it fetches. enableAccordion would mount all of them.
            subRowContent={(tableRow) =>
              allowMembershipMatch &&
              tableRow.original.id === viewingContractId &&
              canMatchMemberships(tableRow.original) ? (
                <MembershipMatchSection contractId={viewingContractId} eventId={event.id} />
              ) : null
            }
          />
        </div>
      )}

      {event.contractId &&
      event.contractId !== 0 &&
      event.contractNumber &&
      event.contractNumber.trim() !== '' ? (
        <div className="mt-4 flex justify-end">
          <Button
            variant="destructive"
            onClick={() => void handleClearContract()}
            disabled={isLoading}
          >
            Clear Contract Number
          </Button>
        </div>
      ) : null}
    </div>
  );

  // Phones and tablets: the whole screen, rising from the bottom, with a worded Close at the top.
  if (isFieldLayout) {
    return (
      <FieldFullScreenDialog
        open={open}
        onOpenChange={(nextOpen) => !nextOpen && onClose()}
        title="Add/Update Contract Number"
        closeLabel="Close"
        bodyClassName="px-4"
      >
        {body}
      </FieldFullScreenDialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* 5xl, not 4xl: the six columns plus both actions measure 879px, which 4xl's 846px content box
          scrolls for no reason. The scroller below stays as the narrow-viewport fallback. */}
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-5xl overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>Add/Update Contract Number</DialogTitle>
        </DialogHeader>

        {body}
      </DialogContent>
    </Dialog>
  );
};
